import {
  importPrivateKey,
  signEnvelope,
  randomNonceB64,
} from '../../shared/sign';
import {
  canonicalCohortScope,
  canonicalDeviceTag,
} from '../../shared/crypto';
import {
  parseAuthMe,
  parseClaimList,
  parseClaimChallenge,
  parseClaimAccepted,
  parseClaimRevoked,
  parseVisibilitySet,
  parseCohortScopeSet,
  parseDeviceTagSet,
} from '../../shared/wire';
import { loadKeypair } from '../storage/persistence';

export async function apiAuthMe(){
  const res = await fetch('/api/v1/auth/me', { credentials: 'include' });
  if(res.status === 200) return parseAuthMe(await res.json()).id;
  return null;
}

export async function apiListClaims(){
  const res = await fetch('/api/v1/claims', { credentials: 'include' });
  if(!res.ok){ const e: any = new Error('claims ' + res.status); e.status = res.status; throw e; }
  return parseClaimList(await res.json());
}

export async function apiClaimChallenge(token: string){
  const res = await fetch('/api/v1/claims/challenge', {
    method: 'POST', credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ device_token: token }),
  });
  if(!res.ok){ const e: any = new Error('challenge ' + res.status); e.status = res.status; throw e; }
  return parseClaimChallenge(await res.json());
}

export async function apiClaimAccept(token: string, nonce: string, signature: string, timestamp: number){
  const res = await fetch('/api/v1/claims/accept', {
    method: 'POST', credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ device_token: token, nonce, signature, timestamp }),
  });
  if(!res.ok){ const e: any = new Error('accept ' + res.status); e.status = res.status; throw e; }
  return parseClaimAccepted(await res.json());
}

export async function apiClaimRevoke(token: string){
  const res = await fetch('/api/v1/claims/revoke', {
    method: 'POST', credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ device_token: token }),
  });
  if(!res.ok){ const e: any = new Error('revoke ' + res.status); e.status = res.status; throw e; }
  return parseClaimRevoked(await res.json());
}

export async function apiSetVisibility(token: string, visibility: string){
  const res = await fetch(`/api/v1/devices/${encodeURIComponent(token)}/visibility`, {
    method: 'POST', credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ visibility }),
  });
  if(!res.ok){ const e: any = new Error('visibility ' + res.status); e.status = res.status; throw e; }
  return parseVisibilitySet(await res.json());
}

// Signed cohort-scope setter — mirrors apiMintShare (key possession, no
// session). The device's ed25519 key signs canonicalCohortScope; the server
// verifies and writes devices.cohort_range / cohort_precision.
export async function apiSetCohortScope(token: string, cohort_scope: { range: string; precision: string }){
  const stored = loadKeypair(token);
  if(!stored) throw new Error('no signing key for ' + token);
  const privKey = await importPrivateKey(stored.priv);
  const timestamp = Date.now();
  const nonce = randomNonceB64();
  const envelope = canonicalCohortScope(token, 'cohort-scope', cohort_scope as any, nonce, timestamp);
  const sig = await signEnvelope(privKey, envelope);
  const res = await fetch(`/api/v1/devices/${encodeURIComponent(token)}/cohort-scope`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'X-Trail-Signature': sig },
    body: JSON.stringify({ token, cohort_scope, nonce, timestamp }),
  });
  if(!res.ok){ const e: any = new Error('cohort-scope ' + res.status); e.status = res.status; throw e; }
  return parseCohortScopeSet(await res.json());
}

// Signed display-tag setter (ADR 0022) — mirrors apiSetCohortScope (key
// possession, no session). The device's ed25519 key signs canonicalDeviceTag;
// '' is normalised to null so the canon matches the server verifier byte-exact.
export async function apiSetDeviceTag(token: string, tag: string | null){
  const stored = loadKeypair(token);
  if(!stored) throw new Error('no signing key for ' + token);
  const privKey = await importPrivateKey(stored.priv);
  const timestamp = Date.now();
  const nonce = randomNonceB64();
  const tagValue = (typeof tag === 'string' && tag.length > 0) ? tag : null;
  const envelope = canonicalDeviceTag(token, 'tag', tagValue, nonce, timestamp);
  const sig = await signEnvelope(privKey, envelope);
  const res = await fetch(`/api/v1/devices/${encodeURIComponent(token)}/tag`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'X-Trail-Signature': sig },
    body: JSON.stringify({ token, tag: tagValue, nonce, timestamp }),
  });
  if(!res.ok){ const e: any = new Error('device-tag ' + res.status); e.status = res.status; throw e; }
  return parseDeviceTagSet(await res.json());
}
